import * as HLF from "html-living-framework";
import * as PIXI from "pixi.js";
import Settings from "Data/Settings";
import * as Topics from "Data/Topics";
import * as Constants from "Data/Constants";
import { TimelineLite } from "gsap";

/** Accessible text */
export class Text extends PIXI.BitmapText implements HLF.Input.IFocusable {

    protected _subscriptions: any[] = [];
    protected _infoVO?: string;
    protected dwellTimeline?: TimelineLite;
    protected isCursorDwelling: boolean = false;

    protected infoButton: PIXI.Sprite;

    protected bg: PIXI.Graphics;

    private readonly baseFontSize: number;
    private readonly dontScale: boolean;
    private readonly dontChangeColour: boolean;

    public constructor(
        pText: string,
        pFont: string,
        pSize: number,
        pAnchor: PIXI.Point,
        public hoverVO?: string,
        pInfoVO?: string,
        pOptions?: {
            dontScale?: boolean,
            dontChangeColour?: boolean,
        },
    ) {
        super(pText, {
            fontName: pFont,
            fontSize: (pOptions?.dontScale ?? false) ? pSize : pSize * Settings.textSizeMultiplier,
            align: "left",
            tint: 0x000000,
        });

        this.baseFontSize = pSize;
        this.dontScale = (pOptions?.dontScale ?? false);
        this.dontChangeColour = (pOptions?.dontChangeColour ?? false);
        this.align = "left";
        this.anchor = pAnchor;

        this.bg = this.addChildAt(new PIXI.Graphics(), 0);

        this._infoVO = pInfoVO;

        this.infoButton = HLF.PixiUtils.makeSprite("cyberchase-settings-btn-componenet-help");
        this.infoButton.interactive = true;
        this.infoButton.buttonMode = true;
        if (this._infoVO === undefined) {
            this.infoButton.visible = false;
        }

        this.addChild(this.infoButton);

        this.infoButton.hitArea = HLF.Input.hitAreaFromSprite(this.infoButton);
        this.infoButton.on(HLF.Input.Events.POINTER_OVER, (event: PIXI.InteractionEvent)=>{
            event.stopPropagation();
            this.playInfoVO();
        });

        this.interactive = true;
        this.interactiveChildren = true;
        this.buttonMode = false;

        this.on(HLF.Input.Events.POINTER_OVER, this.onPointerOver)
        this.on(HLF.Input.Events.POINTER_DOWN, this.onPointerDown);
        this.on(HLF.Input.Events.POINTER_UP, this.onPointerUp);
        this.on(HLF.Input.Events.POINTER_OUT, this.onPointerOut);

        this.redrawBg();

        this._subscriptions.push(
            PubSub.subscribe(Topics.SETTINGS_CHANGED, this.onSettingsChanged.bind(this)),
        );
    }

    /** position the info button */
    setWidth(pWidth: number) {
        this.infoButton.x = (pWidth - this.infoButton.width) / this.scale.x;
    }

    onFocusBegin(): void {
        this.playHoverVO();
    }

    onFocusEnd(): void {
        //
    }

    onFocusActivated(): void {
        this.playInfoVO();
    }

    getFocusPosition(): PIXI.Point {
        const p = this.getGlobalPosition();
        p.x += (this.width / 2);
        return p;
    }

    getFocusSize(): PIXI.IPoint {
        return HLF.RectUtils.size(HLF.PixiUtils.getGlobalBounds(this));
    }

    public setText(pText: string, pSize?: number) {
        this.text = pText;
        if (pSize) {
            this.fontSize = this.dontScale ? pSize : pSize * Settings.textSizeMultiplier;
        }

        this.redrawBg();
    }

    public destroy(pOptions?: Parameters<typeof PIXI.BitmapText.prototype.destroy>[0]): void {
        super.destroy(pOptions);
        this._subscriptions.forEach(PubSub.unsubscribe);
    }

    protected onPointerOver(pEvent: PIXI.InteractionEvent): void {
        this.playHoverVO();
        if (Settings.useDwellActivation) {
            this.beginDwell();
        }
    }

    protected onPointerDown(pEvent: PIXI.InteractionEvent): void {
        this.playInfoVO();
        if (Settings.useDwellActivation) {
            this.cancelDwell();
        }
    }

    protected onPointerUp(pEvent: PIXI.InteractionEvent): void {
        if (Settings.useDwellActivation) {
            this.cancelDwell();
        }
    }

    protected onPointerOut(pEvent: PIXI.InteractionEvent): void {
        if (Settings.useDwellActivation) {
            this.cancelDwell();
        }
    }

    protected playHoverVO(): void {
        if (this.hoverVO !== undefined) {
            HLF.Application.instance.voiceover.playVO(this.hoverVO, { priority: 0, mode: HLF.PlayMode.Override });
        }
    }

    protected playInfoVO(): void {
        if (this._infoVO !== undefined) {
            HLF.Application.instance.voiceover.playVO(this._infoVO, { priority: 0, mode: HLF.PlayMode.Override });
        }
    }

    protected onSettingsChanged(pTopic: typeof Topics.SETTINGS_CHANGED, pSettings: Readonly<typeof Settings>): void {
        if (this.dontScale === false) {
            this.fontSize = this.baseFontSize * pSettings.textSizeMultiplier;
        }
        this.redrawBg();
    }

    protected beginDwell() {
        this.isCursorDwelling = true;
        this.dwellTimeline?.kill();
        this.dwellTimeline?.clear();
        this.dwellTimeline = new TimelineLite();
        this.dwellTimeline.call(()=>PubSub.publishSync(Topics.DWELL_BEGIN, this), undefined, undefined, 0);
        this.dwellTimeline.call(()=>this.completeDwell(), undefined, undefined, Constants.DWELL_DURATION);
    }

    protected cancelDwell(): void {
        this.dwellTimeline?.kill();
        this.dwellTimeline?.clear();
        if (this.isCursorDwelling) {
            this.isCursorDwelling = false;
            PubSub.publishSync(Topics.DWELL_CANCELLED, this);
        }
    }

    protected completeDwell(): void {
        this.dwellTimeline?.kill();
        this.dwellTimeline?.clear();
        PubSub.publishSync(Topics.DWELL_ACTIVATED, this);
        this.isCursorDwelling = false;
        this.onFocusActivated();
    }

    private redrawBg() {
        const padding = this.baseFontSize / 4;

        if (Settings.contrast === 1 && this.dontChangeColour === false) {
            this.tint = 0xffffff;
            this.bg.visible = true;
            this.bg.clear();
            this.bg.beginFill(0x000000);
            let anchor = this.anchor;
            if (typeof anchor === "number") {
                anchor = new PIXI.Point(anchor, anchor);
            }
            this.bg.drawRoundedRect(this.textWidth * -anchor.x - padding, this.textHeight * -anchor.y - padding, this.textWidth + padding * 2, this.textHeight + padding * 2, padding);
            this.bg.endFill();
        }
        else {
            this.tint = 0x000000;
            this.bg.visible = false;
        }
    }
}